import type { IPhotoCaptureMetadata } from './photoCapture.interface';

// Delivery Sheet TypeScript Interfaces
export interface AdminDeliverySheetQuery {
  // Pagination
  page?: number         // หน้าที่ต้องการ (default: 1)
  limit?: number        // จำนวนต่อหน้า (default: 10)
  
  // Date Filter
  delivery_date?: string // รูปแบบ: 'YYYY-MM-DD'
  
  // Driver Filters
  driver_name?: string   // ชื่อคนขับ (ต้อง URL encode สำหรับภาษาไทย)
  driver_phone?: string  // เบอร์โทรคนขับ
  
  // Route & Status
  route_code?: string    // รหัสเส้นทาง
  status?: 'assigned' | 'in_transit' | 'delivered' | 'cancelled'
  shipping_type?: 'pickup' | 'dropoff'
  
  // General Search
  q?: string            // ค้นหาทั่วไป (order codes, driver names)
}

// Admin Delivery Sheet Detail (from detail API)
export interface AdminDeliverySheetDetail {
  delivery_quantity_policy?: 'delivery_plan_v1' | null
  index?: number | null
  planned_eta?: string | null
  eta?: string | null
  rta?: string | null
  start_time?: string | null
  end_time?: string | null
  uuid: string
  order_uuid?: string              // 🆕 UUID ของ order (ถ้าระบุ order_uuid ใน query)
  order_code: string
  route_code: string
  delivery_date: string
  driver_name: string
  driver_phone: string
  shipping_type: string
  status: string | number          // รองรับทั้ง string และ number
  branch_info: {
    uuid: string
    branch_name: string
    branch_code: string
    phone: string
    contact_person: string
  }
  delivery_address: string
  order_info: {
    uuid: string
    order_ref_code: string
    total_amount: number
    item_count: number
    order_date: string
  }
  order_items: Array<{
    requested_qty?: number
    planned_qty?: number | null
    actual_received_qty?: number | null
    planned_remaining_qty?: number | null
    product_name?: string
    quantity?: number
    unit_price?: number
    total_price?: number
  }>
  invoice_info: {
    uuid: string
    invoice_number: string
    status: string
    total_amount: number
    invoice_date: string
    due_date: string
  } | null
  notes: string
  delivery_info: {
    delivered_at: string
    receiver_name: string
    receiver_note: string
    delivery_photos: Array<{
      uuid: string
      original_filename: string
      s3_file_url: string
      image_type: string
      created_at: string
      capture_metadata?: IPhotoCaptureMetadata | null
    }>
    return_items?: Array<{         // 🆕 รายการคืนสินค้า
      uuid: string
      product_name: string
      quantity: number
      reason?: string
    }>
  }
  netbay_preview?: {
    netbay_json: any;
    validation_status?: {
      is_valid?: boolean;
      completeness_score?: number;
      ready_for_integration?: boolean;
    };
    missing_fields?: string[];
    warnings?: string[];
  }
  created_at: string
  updated_at: string
}

export interface AdminDeliverySheetItem {
  uuid: string
  order_uuid?: string             // 🆕 UUID ของ order (ใช้เมื่อดู detail)
  order_code: string              // เช่น "ORPT-1203-0001"
  route_code: string              // เช่น "R11", "ชลบุรี 2"
  delivery_date: string           // "2025-12-18" (YYYY-MM-DD)
  index?: number                  // 🆕 ลำดับการส่ง (0, 1, 2, ...) - ใช้จัดเรียงจุดส่ง
  eta?: string                    // 🆕 Estimated Time of Arrival (ISO 8601: "2026-01-05T10:12:00.000Z")
  planned_eta?: string | null     // ETA ตามแผนเดิม ใช้ยึดตำแหน่งการ์ดบน timeline
  latest_eta?: string | null      // ETA ล่าสุดหลังคำนวณระหว่างขนส่ง
  actual_delivery_time?: string | null // เวลาส่งจริงจาก receipt/เวลาจบงาน
  driver_name: string             // "ชินพรรธน์ (คนขับ)"
  driver_phone: string            // "0638485146"
  shipping_type: 'dropoff' | 'pickup'  // ประเภทการจัดส่ง (dropoff=ส่งถึงที่, pickup=รับเอง)
  
  // 🆕 STATUS - รองรับทั้ง string และ number
  // - 'pending' / 0     = รอดำเนินการ
  // - 'assigned' / 1    = มอบหมายแล้ว (อยู่ใน route)
  // - 'delivered' / 2   = จัดส่งสำเร็จ (หรือกำลังจัดส่งสำหรับ dropoff)
  // - 'cancelled'       = ยกเลิก
  status: string | number
  
  branch_name: string             // ชื่อสาขา เช่น "FNT6 - สมุทรปราการ"
  branch_code?: string            // รหัสสาขา เช่น "P930"
  delivery_address: string        // ที่อยู่จัดส่ง
  total_amount: number            // ยอดรวม (บาท)
  item_count: number              // จำนวนรายการในออเดอร์
  notes: string                   // หมายเหตุการจัดส่ง
  
  // ข้อมูล Invoice (ถ้ามี)
  invoice_info?: {
    uuid: string                  // UUID ของ invoice
    invoice_number: string        // เลขที่ใบแจ้งหนี้ เช่น "IV251218-00001"
    status: 'draft' | 'sent' | 'paid' | 'cancelled' | 'delivered' | string  // สถานะ invoice
    total_amount: number          // ยอดเงินใน invoice
    invoice_date: string          // วันที่ออก invoice
    due_date: string              // วันครบกำหนดชำระ
  } | null
  
  created_at: string
  updated_at: string
}

export interface AdminDeliverySheetResponse {
  status: boolean
  data: AdminDeliverySheetItem[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  message: string
}

export interface DeliverySheetStatistics {
  status: boolean
  data: {
    total_sheets: number
    by_status: {
      assigned: number
      in_transit: number
      delivered: number
      cancelled: number
    }
    by_shipping_type: {
      pickup: number
      dropoff: number
    }
    total_amount: number
    total_items: number
  }
}

export interface AdminDeliverySheetDetail {
  uuid: string
  order_code: string
  route_code: string
  delivery_date: string
  driver_name: string
  driver_phone: string
  shipping_type: string
  status: string
  branch_name: string
  delivery_address: string
  total_amount: number
  item_count: number
  notes: string
  coordinates?: {
    latitude: number
    longitude: number
  }
  created_at: string
  updated_at: string
}

// Default values for forms
export const DDeliverySheetQuery: AdminDeliverySheetQuery = {
  page: 1,
  limit: 10,
  delivery_date: '',
  driver_name: '',
  driver_phone: '',
  route_code: '',
  status: undefined,
  shipping_type: undefined,
  q: ''
};

// Status options for UI
export const DELIVERY_SHEET_STATUS_OPTIONS = [
  { value: 'pending', numValue: 0, label: 'รอดำเนินการ', color: 'default' },
  { value: 'assigned', numValue: 1, label: 'รอส่ง', color: 'info' },
  { value: 'delivered', numValue: 2, label: 'ส่งสำเร็จ', color: 'success' },
  { value: 'cancelled', label: 'ยกเลิก', color: 'error' }
] as const;

// Helper function to get status display info
export const getDeliveryStatusInfo = (status: string | number, shippingType?: string) => {
  // Normalize status to string
  let normalizedStatus = '';
  if (typeof status === 'number') {
    switch (status) {
      case 0: normalizedStatus = 'pending'; break;
      case 1: normalizedStatus = 'assigned'; break;
      case 2: normalizedStatus = 'delivered'; break;
      default: normalizedStatus = 'pending';
    }
  } else {
    normalizedStatus = status || 'pending';
  }
  
  // Special handling for dropoff with status 2 - show as "กำลังจัดส่ง" instead of "ส่งสำเร็จ"
  if (shippingType === 'dropoff' && (status === 2 || normalizedStatus === 'delivered')) {
    // Check if actually delivered or still in transit
    // If status is number 2 and not string 'delivered', it means "กำลังจัดส่ง"
    if (typeof status === 'number') {
      return { label: 'กำลังจัดส่ง', color: 'warning', bgColor: 'warning.light', textColor: 'warning.contrastText' };
    }
  }
  
  switch (normalizedStatus) {
    case 'pending':
      return { label: 'รอดำเนินการ', color: 'default', bgColor: 'grey.300', textColor: 'text.primary' };
    case 'assigned':
      return { label: 'รอส่ง', color: 'info', bgColor: 'info.light', textColor: 'info.contrastText' };
    case 'delivered':
      return { label: 'ส่งสำเร็จ', color: 'success', bgColor: 'success.light', textColor: 'success.contrastText' };
    case 'cancelled':
      return { label: 'ยกเลิก', color: 'error', bgColor: 'error.light', textColor: 'error.contrastText' };
    default:
      return { label: normalizedStatus, color: 'default', bgColor: 'grey.300', textColor: 'text.primary' };
  }
};

export const SHIPPING_TYPE_OPTIONS = [
  { value: 'pickup', label: 'รับของ' },
  { value: 'dropoff', label: 'ส่งของ' }
] as const;
